/**
 * «Погодинні ціни РДН» — той самий графік, що й у розділі «Ціни» (вкладка РДН),
 * але самодостатній: сам тягне дані й не залежить від стану сторінки цін
 * (useMarketPricesUi, вкладки, перемикач графік/таблиця).
 *
 * Зроблено окремим компонентом, а не імпортом з market/prices/page.tsx, бо там
 * графік вшитий у RdnRedesign разом із плитками зон і прив'язаний до спільного
 * стану дати. Тягнути це на головну означало б тягнути й усе решта.
 *
 * Дата: без явного пропа береться остання торгова доба з /market/dam-summary —
 * інакше вранці, поки цін на сьогодні ще немає, графік був би порожній.
 */
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import i18n from '@/i18n';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { useTheme } from '@/hooks/useTheme';
import { API_BASE_URL, authFetch } from '@/services/api';

interface HourRow { hour: number; price: number | null; volume: number | null; hh: string }
interface DamResp {
  tradeDate: string | null;
  hours: { hour: number; price: number | null; volume: number | null }[];
}

const PRICE_TICKS = [0, 2000, 4000, 6000, 8000, 10000, 12000, 14000, 16000];
const loc = () => (i18n.language === 'en' ? 'en-GB' : 'uk-UA');
const num = (n: number | null | undefined, d = 2) =>
  n == null ? '—' : Number(n).toLocaleString(loc(), { minimumFractionDigits: d, maximumFractionDigits: d });

// Recharts не розуміє var(--…), тож токени резолвимо через getComputedStyle.
// Залежність від isDark обов'язкова: при зміні теми значення інші.
function useChartColors() {
  const { isDark } = useTheme();
  return useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const v = (n: string, fb: string) => css.getPropertyValue(n).trim() || fb;
    return {
      // Ціна — приглушений --series-3, а не яскравий accent: той читався як збій.
      price: v('--series-3', '#A6575B'),
      vol: v('--series-2', '#5E656D'),
      grid: v('--line', '#E4E5E7'),
      text: v('--text', '#28323C'),
      muted: v('--text-muted', '#5E656D'),
      dim: v('--text-dim', '#9AA0A6'),
      surface: v('--surface-solid', '#FFFFFF'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDark]);
}

export default function RdnHourlyChart({ date, height = 320, className = '' }:
  { date?: string; height?: number; className?: string }) {
  const { t } = useTranslation();
  const c = useChartColors();
  const [dam, setDam] = useState<DamResp | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let dead = false;
    setLoading(true);

    const load = async () => {
      let d = date;
      if (!d) {
        const s = await authFetch(`${API_BASE_URL}/market/dam-summary`)
          .then((r) => (r.ok ? r.json() : null)).catch(() => null);
        d = s?.tradeDate ?? undefined;
      }
      if (!d) return null;
      return authFetch(`${API_BASE_URL}/market/dam-hourly?date=${d}`)
        .then((r) => (r.ok ? r.json() : null)).catch(() => null);
    };

    load()
      .then((h: DamResp | null) => { if (!dead) setDam(h); })
      .finally(() => { if (!dead) setLoading(false); });

    return () => { dead = true; };
  }, [date]);

  const hours: HourRow[] = useMemo(
    () => (dam?.hours || []).map((h) => ({ ...h, hh: String(h.hour).padStart(2, '0') })),
    [dam],
  );
  const volMax = useMemo(() => {
    const mx = Math.max(0, ...hours.map((h) => h.volume ?? 0));
    return mx > 0 ? Math.ceil(mx / 1000) * 1000 : 5000;
  }, [hours]);

  return (
    <div className={`tile p-4 flex flex-col ${className}`} style={{ minWidth: 0 }}>
      <div className="font-display text-[14px] font-semibold mb-2" style={{ color: 'var(--text)' }}>
        {t('ak_prices_hourly_dam_prices')}
      </div>
      {/* Висота фіксована й задана ззовні: плитка не повинна змінювати розмір
          між станами «завантаження» / «немає даних» / графік — інакше сторінка
          смикається, коли дані приходять. */}
      <div style={{ height, minWidth: 0 }}>
        {loading && !dam ? (
          <div className="flex items-center justify-center h-full text-xs" style={{ color: 'var(--text-muted)' }}>
            {t('ak_prices_loading')}
          </div>
        ) : hours.length === 0 ? (
          <div className="flex items-center justify-center h-full text-xs text-center px-4" style={{ color: 'var(--text-muted)' }}>
            {t('ak_prices_no_dam_data')}<br />{t('ak_prices_prices_for_tomorrow_appear_today_at_12_2')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={hours} margin={{ top: 8, right: 4, bottom: 4, left: 4 }} barCategoryGap="12%">
              <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
              <XAxis dataKey="hh" tick={{ fontSize: 9, fill: c.dim }} axisLine={false} tickLine={false} interval={1} />
              <YAxis yAxisId="price" domain={[0, 16000]} ticks={PRICE_TICKS} width={52}
                tick={{ fontSize: 9, fill: c.dim }} axisLine={false} tickLine={false}
                tickFormatter={(v: number) => v.toLocaleString(loc())} />
              <YAxis yAxisId="vol" orientation="right" domain={[0, volMax]} width={46}
                tick={{ fontSize: 9, fill: c.dim }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ background: c.surface, border: `1px solid ${c.grid}`, borderRadius: 10, fontSize: 12, color: c.text }}
                formatter={(v: any, n: any) => (n === t('ak_prices_price') ? [t('ak_prices_uah_mwh', { n: num(v, 0) }), n] : [t('ak_prices_mwh', { n: num(v, 1) }), n])}
                labelFormatter={(l: string) => t('ak_prices_hour_00', { h: l })} />
              <Legend wrapperStyle={{ fontSize: 11, color: c.muted }} />
              {/* Ширину стовпця задає barCategoryGap (12% слота), а не
                  фіксований maxBarSize: інакше на широкому екрані стовпці
                  лишались вузькими, а проміжки — ширшими за них самих.
                  maxBarSize тут лише стеля, щоб на дуже широкій карті
                  стовпці не розповзлися. */}
              <Bar yAxisId="vol" dataKey="volume" name={t('ak_prices_volume')} fill={c.vol} fillOpacity={0.18}
                stroke={c.vol} strokeWidth={1} maxBarSize={40} radius={[3, 3, 0, 0]} isAnimationActive={false} />
              <Line yAxisId="price" type="monotone" dataKey="price" name={t('ak_prices_price')} stroke={c.price}
                strokeWidth={2.2} dot={false} activeDot={{ r: 4 }} connectNulls isAnimationActive={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
